import type { Request, Response, NextFunction } from "express";
import type { ExtendedRequest } from "./types";
import { setupTryCatchFactory } from "./factory";
import { ExpressErrorUtility } from "./utility";

export { ExpressErrorUtility } from './utility';

/**
 * It returns two handlers - `initialize` and `onError`.
 * Add `initialize` to your app using `app.use` before all route handlers.
 * Add `onError` after all route handlers and before other error handlers.
 *
 * @param MiddlewareOptions - pass project name and your API key here.
 */
export const setupTryCatch = setupTryCatchFactory({
    createContext: (opts) => ({
        instance: new ExpressErrorUtility(opts.projectName, opts.apiKey),
    }),
    handlers: {
        initialize:
            (context) => (req: Request, res: Response, next: NextFunction) => {
                (req as ExtendedRequest).tryCatchContext = {};

                next();
            },
        onError:
            (context) =>
            (
                error: unknown,
                req: Request,
                res: Response,
                next: NextFunction
            ) => {
                const tccReq = req as ExtendedRequest;
                const errorContext = tccReq.tryCatchContext ?? {};

                context.instance
                    .sendErrorFromEndPoint(error, req, errorContext)
                    .catch((e) => {
                        console.warn(e);
                    });

                return next(error);
            },
    },
});

/**
 * Adds context details to try-catch-cloud error.
 * If you call it multiple times, it collects all details,
 * but details with the same name will be overwritten with the latest value.
 *
 * @example
 * addContext(req, { username: 'hugo_reyes8' }); // Context: { username: 'hugo_reyes8' }
 * addContext(req, { age: 29 }); // Context: { username: 'hugo_reyes8', age: 29 }
 * addContext(req, { age: 32 }); // Context: { username: 'hugo_reyes8', age: 32 }
 *
 * @param req - Express request
 * @param details - Context details
 */
export const addContext = (
    req: Request,
    details: Record<string, unknown>
): void => {
    const tccReq = req as ExtendedRequest;

    if (!tccReq.tryCatchContext) {
        (req as ExtendedRequest).tryCatchContext = {};
    }

    tccReq.tryCatchContext = {
        ...tccReq.tryCatchContext,
        ...details,
    };
};
